/**
 * Núcleo puro da medição de voz (spec-008-voz-conversacional-tempo-real-chat,
 * [T001], US-1) — sem rede, sem DOM, sem relógio próprio. O instante entra
 * por parâmetro em toda função que precisa dele; quem tem `performance.now()`
 * ou `Date.now()` de verdade é a camada impura que chama este módulo
 * (`motor-de-voz.js`, [T002]), nunca este arquivo. Mesmo espírito de
 * `aawz_intelligence_v2/core/voz_ack.py:8` ("núcleo puro testável sem
 * ambiente") — este é o par do lado do frontend.
 *
 * Por quê existir: os dois transportes (`porTurnos` hoje, `tempoReal` atrás
 * de bandeira) precisam ser medidos com o MESMO instrumento — medir cada
 * caminho com um jeito diferente produz a comparação que já vem decidida
 * (`plan.md` D-6). `utils/transporte-de-voz.js` e os transportes concretos
 * ([T020]-[T031]) chamam `marcar(nome, instante)` desta fábrica através de
 * `ContextoDeSessao.marcar` (`contracts/transporte.md`) — o NOME do método é
 * o mesmo do contrato, de propósito.
 *
 * O shape da linha (`linha()`) segue `contracts/medicao.md` byte a byte —
 * esse contrato é a spec, não esta descrição em prosa.
 */

// As seis marcas nomeadas do contrato, na mesma ordem da tabela de
// `contracts/medicao.md`. Nomeadas — nunca literais espalhados pelos
// transportes ou pelo `motor-de-voz.js` que os instrumenta.
export const MARCA_INICIO_DA_SESSAO = 'inicio_da_sessao'
export const MARCA_FIM_DA_FALA = 'fim_da_fala'
export const MARCA_PRIMEIRO_AUDIO = 'primeiro_audio'
export const MARCA_PRIMEIRA_FALA_DE_PROGRESSO = 'primeira_fala_de_progresso'
export const MARCA_RESPOSTA_COMPLETA = 'resposta_completa'
export const MARCA_FIM_DA_SESSAO = 'fim_da_sessao'

export const MARCAS_NOMEADAS = Object.freeze([
  MARCA_INICIO_DA_SESSAO,
  MARCA_FIM_DA_FALA,
  MARCA_PRIMEIRO_AUDIO,
  MARCA_PRIMEIRA_FALA_DE_PROGRESSO,
  MARCA_RESPOSTA_COMPLETA,
  MARCA_FIM_DA_SESSAO,
])

/** O `"schema"` da linha por turno — `contracts/medicao.md`. */
export const SCHEMA_MEDICAO_VOZ = 'aawz-medicao-voz/1'

// Os dois valores válidos de `transporte` — os MESMOS que `TransporteDeVoz.
// nome` em `contracts/transporte.md` (\"Vai para a linha de medição\"). Fonte
// única aqui: os transportes concretos ([T020]/[T031]) e a fábrica de
// seleção ([T021]) devem usar estas constantes, não repetir a string.
export const TRANSPORTE_POR_TURNOS = 'porTurnos'
export const TRANSPORTE_TEMPO_REAL = 'tempoReal'
export const TRANSPORTES_VALIDOS = Object.freeze([
  TRANSPORTE_POR_TURNOS,
  TRANSPORTE_TEMPO_REAL,
])

const SEPARADOR_DO_DELTA = '__'

/** A chave de um delta no shape `ms` — ex.: `fim_da_fala__primeiro_audio`. */
export const nomeDoDelta = (de, para) => `${de}${SEPARADOR_DO_DELTA}${para}`

// Os três deltas que `contracts/medicao.md` mostra no exemplo — sempre
// medidos a partir de `fim_da_fala` (é o instante em que o consultor parou
// de falar, a referência de "quanto tempo ele ficou esperando").
const PARES_DE_DELTA = Object.freeze([
  [MARCA_FIM_DA_FALA, MARCA_PRIMEIRO_AUDIO],
  [MARCA_FIM_DA_FALA, MARCA_PRIMEIRA_FALA_DE_PROGRESSO],
  [MARCA_FIM_DA_FALA, MARCA_RESPOSTA_COMPLETA],
])

export const DELTA_FIM_DA_FALA_PRIMEIRO_AUDIO = nomeDoDelta(
  MARCA_FIM_DA_FALA,
  MARCA_PRIMEIRO_AUDIO
)
export const DELTA_FIM_DA_FALA_PRIMEIRA_FALA_DE_PROGRESSO = nomeDoDelta(
  MARCA_FIM_DA_FALA,
  MARCA_PRIMEIRA_FALA_DE_PROGRESSO
)
export const DELTA_FIM_DA_FALA_RESPOSTA_COMPLETA = nomeDoDelta(
  MARCA_FIM_DA_FALA,
  MARCA_RESPOSTA_COMPLETA
)

/**
 * O objeto `ms` da linha (`contracts/medicao.md`) a partir de um mapa
 * `{ [marca]: instanteMs }`. Um par só entra no resultado quando as DUAS
 * marcas existem — é assim que `primeira_fala_de_progresso` (obrigatória só
 * "quando houve tool", ver tabela do contrato) fica de fora sozinha quando o
 * turno não teve tool, sem precisar de um `if (houveTool)` aqui: a AUSÊNCIA
 * da marca já basta.
 */
export const calcularDeltas = (marcas) => {
  const ms = {}
  PARES_DE_DELTA.forEach(([de, para]) => {
    const instanteDe = marcas[de]
    const instantePara = marcas[para]
    if (typeof instanteDe !== 'number' || typeof instantePara !== 'number') {
      return
    }
    ms[nomeDoDelta(de, para)] = instantePara - instanteDe
  })
  return ms
}

const erroDeValidacao = (origem, nomeDoCampo, esperado, recebido) =>
  new Error(
    `${origem} precisa de ${nomeDoCampo} ${esperado}, recebido: ${JSON.stringify(recebido)}`
  )

const assertInteiroPositivo = (valor, nomeDoCampo, origem) => {
  if (!Number.isInteger(valor) || valor < 1) {
    throw erroDeValidacao(origem, nomeDoCampo, 'inteiro >= 1', valor)
  }
}

const assertStringNaoVazia = (valor, nomeDoCampo, origem) => {
  if (typeof valor !== 'string' || valor.trim().length === 0) {
    throw erroDeValidacao(origem, nomeDoCampo, 'como string não vazia', valor)
  }
}

const assertBooleano = (valor, nomeDoCampo, origem) => {
  if (typeof valor !== 'boolean') {
    throw erroDeValidacao(origem, nomeDoCampo, 'booleano', valor)
  }
}

const assertTransporteValido = (transporte) => {
  if (!TRANSPORTES_VALIDOS.includes(transporte)) {
    throw erroDeValidacao(
      'criarRegistroDoTurno',
      'transporte',
      `em [${TRANSPORTES_VALIDOS.join(', ')}]`,
      transporte
    )
  }
}

const assertMarcaValida = (nome) => {
  if (!MARCAS_NOMEADAS.includes(nome)) {
    throw erroDeValidacao(
      'marcar',
      'nome',
      `uma das marcas nomeadas [${MARCAS_NOMEADAS.join(', ')}]`,
      nome
    )
  }
}

const assertInstanteValido = (instante, nome) => {
  if (typeof instante !== 'number' || !Number.isFinite(instante)) {
    throw erroDeValidacao(
      `marcar('${nome}', ...)`,
      'instante',
      'numérico finito, em ms',
      instante
    )
  }
}

// N10: turno que falhou NUNCA some — entra com `falhou: true` e um motivo
// NOMEADO. Sem motivo, `marcarFalha` recusa em vez de deixar a linha entrar
// no artefato com `motivo_da_falha: null`, o que a tornaria indistinguível
// de sucesso no agregado.
const assertMotivoValido = (motivo) => {
  if (typeof motivo !== 'string' || motivo.trim().length === 0) {
    throw erroDeValidacao(
      'marcarFalha',
      'motivo',
      'nomeado (string não vazia) — invariante N10',
      motivo
    )
  }
}

/**
 * Fábrica do registro de UM turno — o objeto que os transportes populam via
 * `marcar`/`marcarFalha` e a task de medição lê via `linha()`.
 *
 * `houveTool` é um parâmetro EXPLÍCITO do chamador, não inferido da presença
 * da marca `primeira_fala_de_progresso`: uma tool que termina antes de
 * `SEGUNDOS_ATE_PRIMEIRO_PROGRESSO` (`voz_ack.py`, 8s) foi chamada mesmo sem
 * nunca falar progresso — inferir pela marca reportaria `houve_tool: false`
 * num turno que teve tool, só porque ela foi rápida. Domínio, não este
 * núcleo, decide se houve tool.
 *
 * @param {Object} params
 * @param {string} params.transporte `'porTurnos'` | `'tempoReal'`
 * @param {number} params.execucao qual das N execuções do roteiro (1-based)
 * @param {string} params.turno o id do turno no roteiro (ex.: `'T1'`)
 * @param {number} params.idEscritorio escritório de teste da medição (US-1)
 * @param {number} params.idProfissional profissional de teste da medição
 * @param {boolean} params.houveTool se uma tool foi chamada neste turno
 * @returns {{marcar: Function, marcarFalha: Function, linha: Function}}
 */
export const criarRegistroDoTurno = ({
  transporte,
  execucao,
  turno,
  idEscritorio,
  idProfissional,
  houveTool,
}) => {
  assertTransporteValido(transporte)
  assertInteiroPositivo(execucao, 'execucao', 'criarRegistroDoTurno')
  assertStringNaoVazia(turno, 'turno', 'criarRegistroDoTurno')
  assertInteiroPositivo(idEscritorio, 'idEscritorio', 'criarRegistroDoTurno')
  assertInteiroPositivo(idProfissional, 'idProfissional', 'criarRegistroDoTurno')
  assertBooleano(houveTool, 'houveTool', 'criarRegistroDoTurno')

  const marcas = {}
  let falhou = false
  let motivoDaFalha = null

  const marcar = (nome, instante) => {
    assertMarcaValida(nome)
    assertInstanteValido(instante, nome)
    // Última chamada vence: um transporte pode remarcar de propósito ao
    // retentar dentro do mesmo turno (ex.: reabrir o canal após queda,
    // US-3 AC5) — o núcleo não tem contexto para distinguir isso de um bug
    // de dupla marcação, então a política é permissiva, por design.
    marcas[nome] = instante
  }

  const marcarFalha = (motivo) => {
    assertMotivoValido(motivo)
    falhou = true
    motivoDaFalha = motivo
  }

  const linha = () =>
    Object.freeze({
      schema: SCHEMA_MEDICAO_VOZ,
      transporte,
      execucao,
      turno,
      id_escritorio: idEscritorio,
      id_profissional: idProfissional,
      ms: Object.freeze(calcularDeltas(marcas)),
      houve_tool: houveTool,
      falhou,
      // N10: mesmo com marcas incompletas (a falha pode ter interrompido o
      // turno antes de todas as marcas seguintes), a linha SEMPRE existe —
      // ela só não tem os deltas que dependiam da marca que nunca chegou.
      motivo_da_falha: falhou ? motivoDaFalha : null,
    })

  return { marcar, marcarFalha, linha }
}

const ordenarAsc = (numeros) => [...numeros].sort((a, b) => a - b)

const mediana = (numeros) => {
  if (numeros.length === 0) return null
  const ordenados = ordenarAsc(numeros)
  const meio = Math.floor(ordenados.length / 2)
  return ordenados.length % 2 === 0
    ? (ordenados[meio - 1] + ordenados[meio]) / 2
    : ordenados[meio]
}

// Nearest-rank, sem interpolação — método simples e determinístico,
// adequado para amostras pequenas (N=5 do roteiro golden, US-1 AC2); uma
// variante interpolada (linear/midpoint) não tem necessidade real aqui e só
// acrescentaria uma decisão a mais para justificar.
const percentil = (numeros, p) => {
  if (numeros.length === 0) return null
  const ordenados = ordenarAsc(numeros)
  const indice = Math.ceil((p / 100) * ordenados.length) - 1
  const indiceLimitado = Math.min(Math.max(indice, 0), ordenados.length - 1)
  return ordenados[indiceLimitado]
}

const PERCENTIL_P90 = 90

// Desvio-padrão AMOSTRAL (n-1, correção de Bessel): as execuções do roteiro
// são uma AMOSTRA (N=5 declarado em US-1 AC2), não a população inteira de
// todas as execuções possíveis. Com 1 valor não há variância observável —
// devolve 0 em vez de dividir por zero.
const desvioPadraoAmostral = (numeros) => {
  if (numeros.length === 0) return null
  if (numeros.length === 1) return 0
  const media = numeros.reduce((soma, numero) => soma + numero, 0) / numeros.length
  const somaDosQuadrados = numeros.reduce(
    (soma, numero) => soma + Math.pow(numero - media, 2),
    0
  )
  return Math.sqrt(somaDosQuadrados / (numeros.length - 1))
}

/**
 * Agrega N linhas (`linha()` de N execuções do roteiro) sobre UM delta
 * escolhido — mediana, p90 e desvio-padrão (US-1 AC2).
 *
 * ★ N10, o invariante mais importante deste módulo: `execucoes` conta TODAS
 * as linhas recebidas, falhas incluídas — nunca um array pré-filtrado. Só o
 * CÁLCULO estatístico (mediana/p90/desvio) usa os valores das linhas que não
 * falharam, porque uma execução falha não produziu um instante real para
 * entrar na conta; mas ela nunca desaparece da CONTAGEM nem da lista de
 * motivos. Descartar a falha em silêncio do cálculo é exatamente o que N10
 * proíbe.
 *
 * @param {Array<Object>} linhas linhas produzidas por `criarRegistroDoTurno(...).linha()`
 * @param {string} chaveDoDelta uma chave de `ms` (ex.: `DELTA_FIM_DA_FALA_PRIMEIRO_AUDIO`)
 */
export const agregarPorDelta = (linhas, chaveDoDelta) => {
  if (!Array.isArray(linhas) || linhas.length === 0) {
    throw new Error(
      `agregarPorDelta precisa de ao menos uma linha, recebido: ${JSON.stringify(linhas)}`
    )
  }
  assertStringNaoVazia(chaveDoDelta, 'chaveDoDelta', 'agregarPorDelta')

  const falhas = linhas.filter((linha) => linha.falhou)
  const valores = linhas
    .filter((linha) => !linha.falhou)
    .map((linha) => linha.ms[chaveDoDelta])
    .filter((valor) => typeof valor === 'number' && Number.isFinite(valor))

  return Object.freeze({
    chave: chaveDoDelta,
    execucoes: linhas.length,
    falhas: falhas.length,
    motivosDeFalha: falhas.map((linha) => linha.motivo_da_falha),
    medianaMs: mediana(valores),
    p90Ms: percentil(valores, PERCENTIL_P90),
    desvioPadraoMs: desvioPadraoAmostral(valores),
  })
}
